import React, { useMemo, useRef } from 'react';
import { useQuery } from 'react-query';
import { AlertTriangle, Search, Loader2 } from 'lucide-react';
import objectiveService from '../../services/objectiveService';
import { documentApi } from '../../services/api';
import { UnifiedEvidenceFindings } from './UnifiedEvidenceFindings';
import type { UnifiedEvidenceFindingsHandle } from './UnifiedEvidenceFindings';
import { sanitizeEnumValue } from '../../utils/displayUtils';
import { DerivedOutputBanner } from './DerivedOutputBanner';

interface EvidenceIntegrityTabProps {
  caseId: string;
}

function sev(s: string | undefined): string { return sanitizeEnumValue(s) || 'info'; }

// --- Tier-aligned insights ---

interface TierInsights {
  priority: number;
  further: number;
  context: number;
  summaryLine: string;
}

function readDocCount(meta: any): number {
  if (!meta) return 0;
  return meta.documentsAnalysed || meta.documentsAnalyzed || 0;
}

function extractTierInsights(gapContent: any | null, incContent: any | null): TierInsights {
  let priority = 0;
  let further = 0;
  let context = 0;
  let documentsAnalysed = 0;

  if (gapContent) {
    // Critical gaps → priority
    for (const g of (gapContent.criticalGaps || [])) {
      const u = sev(g.urgency);
      if (u === 'high') priority++;
      else further++;
    }
    // Category items by impact
    const catKeys = ['missingDocuments', 'incompleteDocuments', 'timelineGaps', 'informationGaps'];
    for (const key of catKeys) {
      const items = gapContent.categories?.[key] || [];
      for (const item of items) {
        const impact = sev(item.impact);
        if (impact === 'critical' || impact === 'high') priority++;
        else if (impact === 'significant' || impact === 'medium') further++;
        else context++;
      }
    }
    // Recommendations → context
    context += (gapContent.recommendations || []).length;
    documentsAnalysed = readDocCount(gapContent.metadata);
  }

  if (incContent) {
    // Critical findings → priority
    priority += (incContent.criticalFindings || []).length;
    // Inconsistencies by severity
    for (const inc of (incContent.inconsistencies || [])) {
      const s = sev(inc.severity);
      if (s === 'critical') priority++;
      else if (s === 'significant') further++;
      else context++;
    }
    // Patterns + recommendations → context
    context += (incContent.patterns || []).length;
    context += (incContent.recommendations || []).length;
    if (!documentsAnalysed) {
      documentsAnalysed = readDocCount(incContent.metadata);
    }
  }

  const total = priority + further + context;
  const docSuffix = documentsAnalysed > 0 ? ` across ${documentsAnalysed} documents` : '';
  const summaryLine = priority > 0
    ? `${total} items analysed${docSuffix}. ${priority} ${priority === 1 ? 'issue' : 'issues'} flagged for priority review.`
    : total > 0
      ? `${total} items analysed${docSuffix}. No issues flagged for priority review.`
      : documentsAnalysed > 0
        ? `Analysis based on ${documentsAnalysed} documents.`
        : 'Analysis complete.';

  return { priority, further, context, summaryLine };
}

// --- Component ---

export const EvidenceIntegrityTab: React.FC<EvidenceIntegrityTabProps> = ({ caseId }) => {
  const findingsRef = useRef<UnifiedEvidenceFindingsHandle>(null);

  const { data: objectives = [] } = useQuery(
    ['objectives', caseId],
    () => objectiveService.getObjectives(caseId),
    {
      enabled: !!caseId,
      refetchInterval: (data) => {
        const gap = data?.find(o => o.type === 'GAP_ANALYSIS');
        const inc = data?.find(o => o.type === 'INCONSISTENCY_DETECTION');
        return (gap?.status === 'PROCESSING' || inc?.status === 'PROCESSING') ? 2000 : false;
      },
    }
  );

  const gapObj = objectives.find(o => o.type === 'GAP_ANALYSIS');
  const incObj = objectives.find(o => o.type === 'INCONSISTENCY_DETECTION');

  const { data: gapOutput } = useQuery(
    ['output', caseId, gapObj?.id],
    () => objectiveService.getOutput(caseId, gapObj!.id),
    { enabled: !!gapObj && gapObj.status === 'UP_TO_DATE' && !!gapObj.lastGeneratedAt, retry: false }
  );
  const { data: incOutput } = useQuery(
    ['output', caseId, incObj?.id],
    () => objectiveService.getOutput(caseId, incObj!.id),
    { enabled: !!incObj && incObj.status === 'UP_TO_DATE' && !!incObj.lastGeneratedAt, retry: false }
  );

  const { data: documents = [] } = useQuery(
    ['documents', caseId],
    () => documentApi.getCaseDocuments(caseId),
    { enabled: !!caseId }
  );

  const gapContent = gapObj?.status === 'UP_TO_DATE' && gapOutput ? (gapOutput.content as any) : null;
  const incContent = incObj?.status === 'UP_TO_DATE' && incOutput ? (incOutput.content as any) : null;
  const insights = useMemo(() => extractTierInsights(gapContent, incContent), [gapContent, incContent]);
  const hasAnyData = gapContent || incContent;

  function getStatus(obj: any): 'loading' | 'error' | 'ready' | 'pending' {
    if (!obj) return 'pending';
    if (obj.status === 'PROCESSING') return 'loading';
    if (obj.status === 'ERROR') return 'error';
    if (obj.status === 'UP_TO_DATE') return 'ready';
    return 'pending';
  }

  const gapStatus = getStatus(gapObj);
  const incStatus = getStatus(incObj);
  const anyLoading = gapStatus === 'loading' || incStatus === 'loading';
  const bothPending = gapStatus === 'pending' && incStatus === 'pending';
  const bothProcessing = gapStatus === 'loading' && incStatus === 'loading';

  return (
    <div className="p-6 h-full overflow-auto space-y-6">
      {/* Header */}
      <div className="mb-2">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-medical-100 mb-1">Evidence Integrity</h3>
        <p className="text-sm text-gray-600 dark:text-medical-400">
          Assess completeness of documentation and identify contradictions across the medical records.
        </p>
      </div>

      <DerivedOutputBanner
        caseId={caseId}
        analysisLabel="evidence integrity analysis"
        evaluatePrompt="I have corrected some chronology events since the evidence integrity analysis was generated. Review the edited events and assess whether the corrections affect any identified gaps, inconsistencies, or completeness findings."
      />

      {/* Key Insights */}
      {hasAnyData && (
        <div className="bg-gray-50 dark:bg-medical-800/50 border border-gray-200 dark:border-medical-700 rounded-lg p-4 space-y-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase text-gray-500 dark:text-medical-400 tracking-wide">
            <Search className="w-3.5 h-3.5" />
            Key Insights
          </div>

          <p className="text-sm text-gray-700 dark:text-medical-300">{insights.summaryLine}</p>

          {anyLoading && (
            <div className="flex flex-wrap gap-1.5">
              {gapStatus === 'loading' && (
                <span className="text-xs text-gray-400 dark:text-medical-500 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Gap analysis loading…
                </span>
              )}
              {incStatus === 'loading' && (
                <span className="text-xs text-gray-400 dark:text-medical-500 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Inconsistency detection loading…
                </span>
              )}
            </div>
          )}

          {/* Tier stat cards — clickable to scroll */}
          <div className="grid grid-cols-3 gap-3">
            <TierCard
              label="Priority Review"
              description="Critical gaps and inconsistencies requiring immediate attention"
              count={insights.priority}
              color={insights.priority > 0 ? 'red' : 'green'}
              loading={anyLoading && !hasAnyData}
              onClick={() => findingsRef.current?.scrollToSection('priority')}
            />
            <TierCard
              label="Further Consideration"
              description="Issues that may warrant additional review"
              count={insights.further}
              color={insights.further > 0 ? 'amber' : 'green'}
              loading={anyLoading && !hasAnyData}
              onClick={() => findingsRef.current?.scrollToSection('further')}
            />
            <TierCard
              label="Reviewed & Noted"
              description="Minor items noted with no significant concerns"
              count={insights.context}
              color="green"
              loading={anyLoading && !hasAnyData}
              onClick={() => findingsRef.current?.scrollToSection('context')}
            />
          </div>
        </div>
      )}

      {/* Loading / pending states when no data yet */}
      {!hasAnyData && bothProcessing && <LoadingState label="Evidence integrity analysis" />}
      {!hasAnyData && bothPending && <PendingState />}
      {!hasAnyData && !bothProcessing && !bothPending && (gapStatus === 'error' || incStatus === 'error') && (
        <ErrorState label="Evidence integrity analysis" />
      )}

      {/* Unified findings view */}
      {hasAnyData && (
        <UnifiedEvidenceFindings
          ref={findingsRef}
          gapContent={gapContent}
          incContent={incContent}
          documents={documents}
        />
      )}
    </div>
  );
};

// --- Shared sub-components ---

const TIER_COLORS = {
  red: 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/30',
  amber: 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/30',
  green: 'text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/30',
} as const;

function TierCard({ label, description, count, color, loading, onClick }: {
  label: string;
  description: string;
  count: number;
  color: keyof typeof TIER_COLORS;
  loading?: boolean;
  onClick?: () => void;
}) {
  if (loading) {
    return (
      <div className="rounded-lg border border-gray-200 dark:border-medical-600 p-3 animate-pulse">
        <div className="h-6 w-8 bg-gray-200 dark:bg-medical-700 rounded mb-1" />
        <div className="h-3 w-16 bg-gray-200 dark:bg-medical-700 rounded" />
      </div>
    );
  }
  return (
    <button
      onClick={onClick}
      className={`rounded-lg border p-3 text-left transition-colors cursor-pointer ${TIER_COLORS[color]}`}
    >
      <p className="text-2xl font-bold">{count}</p>
      <p className="text-xs font-medium opacity-75">{label}</p>
      <p className="text-[10px] opacity-60 mt-1 leading-tight">{description}</p>
    </button>
  );
}

function LoadingState({ label }: { label: string }) {
  return (
    <div className="text-center py-12">
      <Loader2 className="w-10 h-10 text-cyan-500 mx-auto mb-3 animate-spin" />
      <p className="text-sm text-gray-600 dark:text-medical-400">{label} is being generated…</p>
    </div>
  );
}

function ErrorState({ label }: { label: string }) {
  return (
    <div className="text-center py-12">
      <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-3" />
      <p className="text-sm text-gray-600 dark:text-medical-400">{label} failed during extraction.</p>
    </div>
  );
}

function PendingState() {
  return (
    <div className="text-center py-12">
      <Search className="w-10 h-10 text-gray-300 dark:text-medical-600 mx-auto mb-3" />
      <p className="text-sm text-gray-600 dark:text-medical-400 max-w-md mx-auto">
        The uploaded evidence did not contain sufficient information to generate an evidence integrity analysis.
      </p>
    </div>
  );
}
